/**
 * useAdminTable - Custom hook for admin list view state management
 * 
 * Handles:
 * - Server-side sorting
 * - Server-side filtering with URL persistence
 * - Pagination
 * - Selection state
 * - Loading state
 * 
 * IMPORTANT: This hook uses useStableSearchParams to prevent infinite loops
 * that can crash mobile browsers. Do NOT use raw useSearchParams in this hook.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useStableSearchParams } from './useStableSearchParams';

/**
 * Build query params object from state
 */
const buildQueryParams = (state) => {
  const params = {
    page: state.page,
    page_size: state.pageSize,
  };
  
  if (state.sortBy) {
    params.sort_by = state.sortBy;
    params.sort_dir = state.sortDir || 'desc';
  }
  
  if (state.search) {
    params.q = state.search;
  }
  
  // Add filters
  if (state.filters && Object.keys(state.filters).length > 0) {
    Object.entries(state.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'all') {
        params[key] = value;
      }
    });
  }
  
  return params;
};

/**
 * Parse URL search params into state
 */
const parseInitialParams = (initialParams, defaults) => {
  return {
    page: parseInt(initialParams.page) || defaults.page,
    pageSize: parseInt(initialParams.page_size) || defaults.pageSize,
    sortBy: initialParams.sort_by || defaults.sortBy,
    sortDir: initialParams.sort_dir || defaults.sortDir,
    search: initialParams.q || '',
    // Parse other filters
    filters: defaults.filterKeys?.reduce((acc, key) => {
      const value = initialParams[key];
      if (value) acc[key] = value;
      return acc;
    }, {}) || {}
  };
};

/**
 * Main hook for admin table state management
 */
export const useAdminTable = ({
  // Default values
  defaultSortBy = 'created_at',
  defaultSortDir = 'desc',
  defaultPageSize = 25,
  
  // Filter configuration - memoized internally to prevent reference changes
  filterKeys = [],
  
  // Initial filters (for "View All" functionality - filters passed from parent)
  initialFilters = {},
  
  // URL persistence
  persistToUrl = true,
  
  // Fetch function
  fetchFn,
  
  // Transform response
  transformResponse = (data) => ({
    items: data.items || [],
    total: data.total || 0
  })
} = {}) => {
  // Use stable search params hook to prevent infinite loops
  const { initialParams, setParams, getParam, removeParams } = useStableSearchParams();
  
  // Memoize filterKeys to prevent reference changes from causing re-renders
  // This is critical - inline arrays like ['status', 'filter'] create new refs each render
  const filterKeysRef = useRef(filterKeys);
  const stableFilterKeys = useMemo(() => {
    // Only update if the actual values changed
    const keysChanged = filterKeys.length !== filterKeysRef.current.length ||
      filterKeys.some((key, i) => key !== filterKeysRef.current[i]);
    if (keysChanged) {
      filterKeysRef.current = filterKeys;
    }
    return filterKeysRef.current;
  }, [filterKeys]);
  
  // Parse initial state from URL or use defaults - runs once on mount
  const initialState = useMemo(() => {
    if (persistToUrl) {
      const parsed = parseInitialParams(initialParams, {
        page: 1,
        pageSize: defaultPageSize,
        sortBy: defaultSortBy,
        sortDir: defaultSortDir,
        filterKeys: stableFilterKeys
      });
      // Merge initialFilters with URL filters (URL takes precedence)
      return {
        ...parsed,
        filters: { ...initialFilters, ...parsed.filters }
      };
    }
    return {
      page: 1,
      pageSize: defaultPageSize,
      sortBy: defaultSortBy,
      sortDir: defaultSortDir,
      search: '',
      filters: { ...initialFilters }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only read URL on mount
  
  // State
  const [page, setPage] = useState(initialState.page);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortDir, setSortDir] = useState(initialState.sortDir);
  const [search, setSearch] = useState(initialState.search);
  const [filters, setFilters] = useState(initialState.filters);
  
  // Data state
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Density state
  const [density, setDensity] = useState(() => {
    return localStorage.getItem('adminTableDensity') || 'comfortable';
  });
  
  // Use ref for functions to avoid dependency issues
  const fetchFnRef = useRef(fetchFn);
  const transformResponseRef = useRef(transformResponse);
  
  // Keep refs updated
  useEffect(() => {
    fetchFnRef.current = fetchFn;
    transformResponseRef.current = transformResponse;
  });
  
  // Track if this is the first render (skip URL update on mount)
  const isFirstRender = useRef(true);
  
  // Update URL when state changes
  // Uses stable setParams which batches and debounces updates
  useEffect(() => {
    // Skip URL update on first render - we just read from URL
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    if (!persistToUrl) return;
    
    // Build params to set
    const urlParams = {};
    
    // Preserve tab if it exists
    const currentTab = getParam('tab');
    if (currentTab) {
      urlParams.tab = currentTab;
    }
    
    // Only set non-default values to keep URL clean
    if (page > 1) urlParams.page = page.toString();
    if (pageSize !== defaultPageSize) urlParams.page_size = pageSize.toString();
    if (sortBy && sortBy !== defaultSortBy) urlParams.sort_by = sortBy;
    if (sortDir && sortDir !== defaultSortDir) urlParams.sort_dir = sortDir;
    if (search) urlParams.q = search;
    
    // Add active filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        urlParams[key] = value;
      }
    });
    
    // Calculate which params to remove (params that are now default/empty)
    const paramsToRemove = [];
    if (page <= 1) paramsToRemove.push('page');
    if (pageSize === defaultPageSize) paramsToRemove.push('page_size');
    if (!sortBy || sortBy === defaultSortBy) paramsToRemove.push('sort_by');
    if (!sortDir || sortDir === defaultSortDir) paramsToRemove.push('sort_dir');
    if (!search) paramsToRemove.push('q');
    
    // Remove filters that are empty or 'all'
    stableFilterKeys.forEach(key => {
      if (!filters[key] || filters[key] === 'all') {
        paramsToRemove.push(key);
      }
    });
    
    // Remove old params first, then set new ones
    if (paramsToRemove.length > 0) {
      removeParams(paramsToRemove);
    }
    
    // Set new params (batched with removes)
    if (Object.keys(urlParams).length > 0) {
      setParams(urlParams);
    }
  }, [page, pageSize, sortBy, sortDir, search, filters, persistToUrl, 
      defaultPageSize, defaultSortBy, defaultSortDir, stableFilterKeys,
      setParams, removeParams, getParam]);
  
  // Save density preference
  useEffect(() => {
    localStorage.setItem('adminTableDensity', density);
  }, [density]);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    const currentFetchFn = fetchFnRef.current;
    const currentTransformResponse = transformResponseRef.current;
    
    if (!currentFetchFn) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = buildQueryParams({
        page,
        pageSize,
        sortBy,
        sortDir,
        search,
        filters
      });
      
      const response = await currentFetchFn(queryParams);
      const { items, total: totalCount } = currentTransformResponse(response);
      
      setData(items);
      setTotal(totalCount);
      
      // Clear selection when data changes
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.message || 'Failed to load data');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, search, filters]);
  
  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Handlers
  const handleSort = useCallback((column, direction) => {
    setSortBy(column);
    setSortDir(direction);
    setPage(1); // Reset to first page on sort change
  }, []);
  
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);
  
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page on page size change
  }, []);
  
  const handleSearch = useCallback((query) => {
    setSearch(query);
    setPage(1); // Reset to first page on search
  }, []);
  
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(1); // Reset to first page on filter change
  }, []);
  
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);
  
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearch('');
    setPage(1);
  }, []);
  
  const handleSelectionChange = useCallback((ids) => {
    setSelectedIds(ids);
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);
  
  const handleDensityChange = useCallback((newDensity) => {
    setDensity(newDensity);
  }, []);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return search || Object.values(filters).some(v => v && v !== 'all');
  }, [search, filters]);
  
  return {
    // Data
    data,
    total,
    loading,
    error,
    
    // Pagination
    page,
    pageSize,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    
    // Sorting
    sortBy,
    sortDir,
    handleSort,
    
    // Search & Filters
    search,
    setSearch: handleSearch,
    filters,
    setFilter: handleFilterChange,
    setFilters: handleFiltersChange,
    clearFilters,
    hasActiveFilters,
    
    // Selection
    selectedIds,
    setSelectedIds: handleSelectionChange,
    clearSelection,
    selectedCount: selectedIds.length,
    
    // Density
    density,
    setDensity: handleDensityChange,
    
    // Actions
    refresh: fetchData,
    
    // Query params for API call
    queryParams: buildQueryParams({
      page,
      pageSize,
      sortBy,
      sortDir,
      search,
      filters
    })
  };
};

/**
 * Hook for managing bulk selection state
 */
export const useBulkSelection = (items = [], getItemId = (item) => item.id) => {
  const [selectedIds, setSelectedIds] = useState([]);
  
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < items.length;
  const noneSelected = selectedIds.length === 0;
  
  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(getItemId));
    }
  }, [allSelected, items, getItemId]);
  
  const toggleSelectItem = useCallback((itemId) => {
    setSelectedIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);
  
  const selectItems = useCallback((ids) => {
    setSelectedIds(ids);
  }, []);
  
  // Get selected items
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.includes(getItemId(item)));
  }, [items, selectedIds, getItemId]);
  
  return {
    selectedIds,
    selectedItems,
    selectedCount: selectedIds.length,
    allSelected,
    someSelected,
    noneSelected,
    toggleSelectAll,
    toggleSelectItem,
    clearSelection,
    selectItems
  };
};

export default useAdminTable;
